import { onSnapshot } from '../firebase/firestore';

// Every onSnapshot listener in this app needs an error handler — without
// one, a single transient failure (most commonly a permission-denied right
// after login, before Firestore's connection has caught up to a freshly
// issued ID token carrying new custom claims — more likely on a slower
// mobile connection) leaves the callback never firing again, and whatever
// `loading` state gated on it stuck true forever until a full page reload
// remounts everything. These wrappers retry a few times with a short delay
// (that race is almost always resolved within a couple of seconds) before
// giving up — logging either way, so a persistent failure is at least
// visible in the console instead of just an infinite spinner.
//
// A give-up used to be permanent: the only way back was a full reload. It
// now also arms a one-shot recovery listener (tab refocus / back online)
// that resets the retry count and tries again — covers the mobile case of
// "locked the phone / lost signal for a bit, came back to a page that never
// recovered" without needing every page to reload itself. It also retries
// on a slow timer, so a page that simply stays open recovers too.
//
// Retries back off exponentially (1.5s, 3s, 6s, 12s, 15s, 15s — about a
// minute in total). The old fixed 3 × 1.5s gave up after ~4.5s, which a
// patchy mobile connection regularly outlasted, leaving an empty list on
// screen until a manual refresh.
const DEFAULT_RETRIES = 6;
const MAX_DELAY_MS = 15000;
const RECOVERY_INTERVAL_MS = 60000;

function backoff(delayMs, attempt) {
  return Math.min(delayMs * 2 ** (attempt - 1), MAX_DELAY_MS);
}

function armRecovery(onRecover) {
  if (typeof document === 'undefined') return () => {};
  let armed = true;
  const tryRecover = () => {
    if (!armed || document.hidden) return;
    armed = false;
    cleanup();
    onRecover();
  };
  const timer = setInterval(tryRecover, RECOVERY_INTERVAL_MS);
  const cleanup = () => {
    clearInterval(timer);
    document.removeEventListener('visibilitychange', tryRecover);
    window.removeEventListener('online', tryRecover);
  };
  document.addEventListener('visibilitychange', tryRecover);
  window.addEventListener('online', tryRecover);
  return cleanup;
}

// Low-level primitives — `onNext` receives the raw snapshot, so call sites
// with their own sort/filter/merge logic in the success callback can keep it
// verbatim and just swap `onSnapshot(query, cb)` for
// `subscribeWithRetry(query, cb, { onGiveUp: () => setLoading(false) })`.
export function subscribeWithRetry(query, onNext, { retries = DEFAULT_RETRIES, delayMs = 1500, onGiveUp } = {}) {
  let cancelled = false;
  let unsub = null;
  let attempt = 0;
  let clearRecovery = null;

  const subscribe = () => {
    unsub = onSnapshot(query, (snap) => {
      attempt = 0;
      onNext(snap);
    }, (err) => {
      console.error('Firestore collection listener error:', err?.code || err?.message || err);
      if (cancelled) return;
      if (attempt < retries) {
        attempt += 1;
        setTimeout(() => { if (!cancelled) subscribe(); }, backoff(delayMs, attempt));
      } else {
        if (onGiveUp) onGiveUp();
        clearRecovery = armRecovery(() => { if (!cancelled) { attempt = 0; subscribe(); } });
      }
    });
  };
  subscribe();

  return () => { cancelled = true; if (unsub) unsub(); if (clearRecovery) clearRecovery(); };
}

export function subscribeDocWithRetry(ref, onNext, { retries = DEFAULT_RETRIES, delayMs = 1500, onGiveUp } = {}) {
  let cancelled = false;
  let unsub = null;
  let attempt = 0;
  let clearRecovery = null;

  const subscribe = () => {
    unsub = onSnapshot(ref, (snap) => {
      attempt = 0;
      onNext(snap);
    }, (err) => {
      console.error('Firestore doc listener error:', err?.code || err?.message || err);
      if (cancelled) return;
      if (attempt < retries) {
        attempt += 1;
        setTimeout(() => { if (!cancelled) subscribe(); }, backoff(delayMs, attempt));
      } else {
        if (onGiveUp) onGiveUp();
        clearRecovery = armRecovery(() => { if (!cancelled) { attempt = 0; subscribe(); } });
      }
    });
  };
  subscribe();

  return () => { cancelled = true; if (unsub) unsub(); if (clearRecovery) clearRecovery(); };
}

// Convenience wrappers for the common case: map docs straight to
// {id, ...data()} and hand them to setData/setLoading.
export function subscribeCollection(query, setData, setLoading, opts = {}) {
  return subscribeWithRetry(
    query,
    (snap) => {
      setData(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    },
    { ...opts, onGiveUp: () => setLoading(false) },
  );
}

export function subscribeDoc(ref, setData, setLoading, opts = {}) {
  return subscribeDocWithRetry(
    ref,
    (snap) => {
      setData(snap.exists() ? { id: snap.id, ...snap.data() } : null);
      setLoading(false);
    },
    { ...opts, onGiveUp: () => setLoading(false) },
  );
}
